/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.25.0
 */

import { FC, useCallback } from 'react';

import { Form, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import FormFieldCheckbox from '@/main/modules/form/components/FormFieldCheckbox';
import { makeSelectTranslationPageSettings } from '@/main/modules/internationalization/intSelectors';
import ButtonServiceUserDenied from '@/main/modules/role/components/ButtonServiceUserDenied';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { ENotificationsSettingsFields } from '@/main/modules/userNotifications/constants';
import {
    makeSelectAuthoritiesIsAssistant,
    makeSelectFeatureEnabled,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { SettingsNotificationsFormControls } from '../styled/SettingsNotificationsStyled';
import { TNotificationsSettingsForm } from '../types';


interface ISettingsNotificationsFormProps {
    defaultFormValues: TNotificationsSettingsForm;
}

const SettingsNotificationsForm: FC<ISettingsNotificationsFormProps> = ({ defaultFormValues }) => {
    const {
        settingsNotifications: { form: tForm },
    } = useSelector(makeSelectTranslationPageSettings());

    const isNewsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.NEWS));
    const isCashbackNotificationEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CASHBACK_NOTIFICATIONS));

    const { values, setValues, isSubmitting, isValid, isValidating, dirty } =
        useFormikContext<TNotificationsSettingsForm>();

    const isAssistant = useSelector(makeSelectAuthoritiesIsAssistant());
    const isCustomInquiriesRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_INQUIRIES_MANAGE);
    const isCustomDocsRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);
    const isCustomCashbackRoleCheckAvailable =
        useCheckCustomRole(false, AuthorityRoleEnum.ROLE_CASHBACK_MANAGE);
    const isCvmNotificationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CVM_NOTIFICATIONS));

    const submitDisabled = isSubmitting || isValidating || !isValid || !dirty;

    const fieldsDisabled = isSubmitting || isValidating;

    const notificationsIsDisabled = !(values.showNotifications || isCvmNotificationsEnabled);

    const handleShowNotificationsChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                const enabledValues = { ...defaultFormValues };
                for (const key in defaultFormValues) {
                    enabledValues[key as ENotificationsSettingsFields] = true;
                }
                setValues(enabledValues);
            } else {
                setValues(defaultFormValues);
            }
        },
        [defaultFormValues, setValues],
    );

    return (
        <Form>
            {!isCvmNotificationsEnabled && (
                <FormFieldCheckbox
                    dataName="settings-showNotifications"
                    disabled={fieldsDisabled}
                    label={tForm.labels[ENotificationsSettingsFields.showNotifications]}
                    name={ENotificationsSettingsFields.showNotifications}
                    onChange={handleShowNotificationsChange}
                />
            )}
            {isNewsFeature && (
                <FormFieldCheckbox
                    dataName="settings-showNews"
                    disabled={fieldsDisabled || notificationsIsDisabled}
                    label={tForm.labels[ENotificationsSettingsFields.showNews]}
                    name={ENotificationsSettingsFields.showNews}
                />
            )}
            {isCashbackNotificationEnabled && (!isAssistant || isCustomCashbackRoleCheckAvailable) && (
                <FormFieldCheckbox
                    dataName="settings-showCashback"
                    disabled={fieldsDisabled || notificationsIsDisabled}
                    label={tForm.labels[ENotificationsSettingsFields.showNotificationsCashback]}
                    name={ENotificationsSettingsFields.showNotificationsCashback}
                />
            )}
            <FormFieldCheckbox
                dataName="settings-showNotificationsOrders"
                disabled={fieldsDisabled || notificationsIsDisabled}
                label={tForm.labels[ENotificationsSettingsFields.showNotificationsOrders]}
                name={ENotificationsSettingsFields.showNotificationsOrders}
            />
            {(!isAssistant || isCustomInquiriesRoleCheckAvailable) && (
                <FormFieldCheckbox
                    dataName="settings-showNotificationsInquiries"
                    disabled={fieldsDisabled || notificationsIsDisabled}
                    label={tForm.labels[ENotificationsSettingsFields.showNotificationsInquiries]}
                    name={ENotificationsSettingsFields.showNotificationsInquiries}
                />
            )}
            {(!isAssistant || isCustomDocsRoleCheckAvailable) && (
                <FormFieldCheckbox
                    dataName="settings-showNotificationsEdo"
                    disabled={fieldsDisabled || notificationsIsDisabled}
                    label={tForm.labels[ENotificationsSettingsFields.showNotificationsEdo]}
                    name={ENotificationsSettingsFields.showNotificationsEdo}
                />
            )}
            <SettingsNotificationsFormControls>
                <ButtonServiceUserDenied disabled={submitDisabled} type="submit">
                    {tForm.controls.submit}
                </ButtonServiceUserDenied>
            </SettingsNotificationsFormControls>
        </Form>
    );
};

export default SettingsNotificationsForm;
